import { useEffect, useState } from "react";
import { useQuery, keepPreviousData } from "@tanstack/react-query";
import { Job } from "@/types";
import { searchJobsAlgolia, AlgoliaJobHit } from "@/lib/algoliaSearch";

// Debounce a fast-changing value (e.g. a search input) for instant-search queries.
export const useDebouncedValue = <T,>(value: T, delayMs = 250): T => {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delayMs);
    return () => clearTimeout(t);
  }, [value, delayMs]);
  return debounced;
};

// Transform an Algolia hit into the Job shape JobCard expects (mirrors useJobs' transformJobData).
const transformAlgoliaHit = (hit: AlgoliaJobHit): Job => {
  const postDate = hit.posted ? new Date(hit.posted * 1000) : new Date();
  const highlight = hit._highlightResult?.title;
  // Honest match-quality signal: every query word hit the title with zero typos.
  const ri = hit._rankingInfo;
  const matchQuality =
    highlight?.matchLevel === "full" && ri
      ? ri.nbTypos === 0
        ? ("exact" as const)
        : ("close" as const)
      : undefined;
  return {
    id: hit.objectID,
    type: "Full Time",
    url: hit.url || `https://jobs.auditfriendly.co/jobs/${hit.objectID}`,
    created_at: postDate.toISOString(),
    company: hit.company || "",
    company_url: null,
    location: hit.location || "",
    display_location: hit.location ?? null,
    display_salary: hit.salary ?? null,
    title: hit.title,
    titleHighlight: highlight && highlight.matchLevel !== "none" ? highlight.value : undefined,
    matchQuality,
    description: "",
    how_to_apply: "Apply through our website",
    company_logo: hit.logo || null,
    salary: hit.salary ?? "",
    is_remote: hit.is_remote || false,
    logo: hit.logo ? (hit.logo.startsWith("http") ? hit.logo : `https://${hit.logo}`) : undefined,
    apply_link: undefined,
    post_date: postDate,
    requirements: [],
    isFeatured: hit.featured || false,
    hours: hit.hours || "Full-Time",
    // Legacy properties
    postDate,
    isRemote: hit.is_remote || false,
    applyLink: undefined,
  } as Job;
};

interface AlgoliaJobsParams {
  query: string;
  location?: string;
  isRemote?: boolean;
  hours?: string[];
  extraKeywords?: string;
  page?: number;
  pageSize?: number;
  enabled?: boolean;
}

// Instant search over the Algolia `af_search` index (jobs posted in the last 60 days).
// Returns the same shape as useJobs so the job list / pagination need no changes.
export const useAlgoliaJobs = ({
  query,
  location,
  isRemote,
  hours,
  extraKeywords,
  page = 1,
  pageSize = 20,
  enabled = true,
}: AlgoliaJobsParams) => {
  const searchQuery = useQuery({
    queryKey: [
      "algoliaJobs",
      query,
      location ?? "",
      !!isRemote,
      (hours ?? []).join("|"),
      extraKeywords ?? "",
      page,
      pageSize,
    ],
    queryFn: () =>
      searchJobsAlgolia({
        query,
        location,
        isRemote,
        hours,
        extraKeywords,
        page,
        hitsPerPage: pageSize,
      }),
    enabled,
    staleTime: 30000,
    placeholderData: keepPreviousData,
  });

  return {
    data: (searchQuery.data?.hits ?? []).map(transformAlgoliaHit),
    isLoading: searchQuery.isLoading,
    isFetching: searchQuery.isFetching,
    totalCount: searchQuery.data?.nbHits ?? 0,
    facets: searchQuery.data?.facets ?? {},
    processingTimeMS: searchQuery.data?.processingTimeMS ?? 0,
    isError: searchQuery.isError,
    error: searchQuery.error,
  };
};
